import React from 'react';
import { get } from 'lodash';
import Button from '@atlaskit/button';
import Tooltip from '@atlaskit/tooltip';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { EntitlementIcon, formattedV2 } from '@css-xp/base';
import { NoData } from '@css-xp/ppc-common';
import { isJiraProduct } from '@atlassian/cssxp-utils-product';
import { InvalidExistingEntitlementMessage } from '../invalid-existing-entitlement-message';
import { InvalidEntitlementLinkMessage } from './invalid-entitlement-link-message';
import { ParentEntitlementDetails } from '../types';
import './parent-product-entitlement-table.scss';

export type ParentProductEntitlementTableProps = {
  entitlementDetails: ParentEntitlementDetails | undefined;
  setIsNotParentProductCompatible: (param: boolean) => void;
  setEnabledEntitlementChangeButton: (param: boolean) => void;
  offeringLabel?: string;
  isInvalidInvoiceGroupId: boolean;
  isNotParentProductCompatible?: boolean;
  isMaxUsersDetailLoading?: boolean;
  onChange?: () => void;
  shouldHideChangeEntitlementButton?: boolean;
};

const Platform = {
  CLOUD: 'CLOUD',
};

const maxUserInfoContent =
  'User tiers of Marketplace apps are matched their parent product, and price calculated accordingly. In the case of multiple Jira products, they’re billed with the product that has the highest user tier.';

export const ParentProductEntitlementTable = ({
  entitlementDetails,
  setIsNotParentProductCompatible,
  setEnabledEntitlementChangeButton,
  offeringLabel,
  isInvalidInvoiceGroupId,
  isNotParentProductCompatible,
  isMaxUsersDetailLoading,
  onChange,
  shouldHideChangeEntitlementButton = false,
}: ParentProductEntitlementTableProps) => {
  const entitlement = entitlementDetails as ParentEntitlementDetails;
  const isItAJiraProduct = isJiraProduct(get(entitlement, 'productId'));

  const [isInvalidEntitlement, setIsInvalidEntitlement] = React.useState(isInvalidInvoiceGroupId);

  React.useEffect(() => {
    setIsInvalidEntitlement(isInvalidInvoiceGroupId);
  }, [isInvalidInvoiceGroupId]);

  return (
    <div className="parent-product-section" data-testid="linked-entitlement-parent-table">
      <h5 className="parent-product-title">Details</h5>
      <div className="parent-product-offering-detail">
        <div className="plan-details">
          <div className="plan-title" data-testId="apps-active-users-label">
            Active users
          </div>
          <div className="plan-value" data-testId="apps-active-users-value">
            {isMaxUsersDetailLoading ? (
              <SkeletonLoaderWrapper>
                <SkeletonLoader width="50px" height="20px" marginBottom={'0'} />
              </SkeletonLoaderWrapper>
            ) : (
              <>
                <span className="pr-4">{get(entitlement, 'maxUnitCount')} </span>
                {isItAJiraProduct && (
                  <Tooltip content={maxUserInfoContent} tag="span">
                    <InfoIcon label="Info" size="small" />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
        <div className="plan-details">
          <div className="plan-title" data-testId="apps-plan-label">
            Plan
          </div>
          <div className="plan-value" data-testId="apps-plan-value">
            {offeringLabel}
          </div>
        </div>
      </div>
      <h5 className="parent-product-title">Linked product</h5>
      <div className="parent-product-detail mt-16">
        <div className="parent-product-name-with-icon">
          <EntitlementIcon
            logoId={get(entitlement, 'logoId') as string}
            productId={get(entitlement, 'productId') as string}
            className="header-icon"
            isCloud={true} // always sending true for now. Currently we have confluence and jira entitlements only
          />
          <div className="parent-product-name-header">
            {entitlement?.productName} - {entitlement?.edition}{' '}
          </div>
        </div>
        <table>
          <tr>
            <th>Entitlement #</th>
            <th>Active&nbsp;users</th>
            <th>Next bill/expiry</th>
            <th></th>
          </tr>
          <tr>
            <td>{entitlement?.entitlementNumber ? get(entitlement, 'entitlementNumber') : <NoData />}</td>
            <td>{get(entitlement, 'unitCount')}</td>
            <td>
              {entitlement?.latestPeriodEndDate ? (
                <formattedV2.FormattedDate
                  dateFormat={NEW_DATE_FORMAT}
                  date={get(entitlement, 'latestPeriodEndDate')}
                />
              ) : (
                <NoData />
              )}
            </td>
            <td>
              {!shouldHideChangeEntitlementButton && (
                <Button
                  appearance="link"
                  type="button"
                  onClick={() => {
                    setIsNotParentProductCompatible(false);
                    setEnabledEntitlementChangeButton(false);
                    onChange && onChange();
                  }}
                  data-testId="change-parent-entitlement"
                >
                  Change
                </Button>
              )}
            </td>
          </tr>
        </table>
        {isNotParentProductCompatible && <InvalidEntitlementLinkMessage />}
        {!isNotParentProductCompatible && isInvalidEntitlement && <InvalidExistingEntitlementMessage />}
      </div>
    </div>
  );
};
